import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, addDoc } from 'firebase/firestore';
import { AlertTriangle, Download, Send, X, Loader2, Search, Filter, FileText, CheckCircle } from 'lucide-react';

const WarningLettersWidget = ({ userRole, userDivision, userName }) => {
  const [defaulters, setDefaulters] = useState([]);
  const [filteredDefaulters, setFilteredDefaulters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [letterType, setLetterType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sendingLetter, setSendingLetter] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sentUndertakings, setSentUndertakings] = useState({});

  useEffect(() => {
    fetchDefaulters();
  }, [userRole, userDivision]);

  useEffect(() => {
    filterDefaulters();
  }, [searchTerm, divisionFilter, defaulters]);

  const fetchDefaulters = async () => {
    try {
      setLoading(true);
      setError(null);

      // Debug: Check what values are being passed to the component
      console.log('=== WARNING LETTERS WIDGET DEBUG ===');
      console.log('userRole:', userRole, 'Type:', typeof userRole);
      console.log('userDivision:', userDivision, 'Type:', typeof userDivision);
      console.log('userName:', userName);
      console.log('===================================');

      const studentsSnapshot = await getDocs(collection(db, 'students'));
      const studentsMap = {};
      studentsSnapshot.forEach(doc => {
        const data = doc.data();
        studentsMap[data.rollNumber] = data;
      });

      const attendanceSnapshot = await getDocs(collection(db, 'attendance'));
      const defaultersList = [];
      const sentMap = {};

      for (const attendanceDocRef of attendanceSnapshot.docs) {
        const attendanceData = attendanceDocRef.data();
        const overallPercentage = parseFloat(attendanceData.overallPercentage) || 0;

        if (overallPercentage < 75) {
          const studentData = studentsMap[attendanceData.rollNumber];
          
          if (studentData) {
            // Filter by division for coordinators
            const isCoordinator = userRole === 'class_coordinator';
            const studentDivision = studentData.division || '';
            
            console.log('Processing student:', {
              rollNumber: attendanceData.rollNumber,
              studentDivision: studentDivision,
              userRole: userRole,
              userDivision: userDivision,
              isCoordinator: isCoordinator,
              shouldFilter: isCoordinator && userDivision,
              willInclude: !(isCoordinator && userDivision && studentDivision !== userDivision)
            });
            
            if (isCoordinator && userDivision) {
              if (studentDivision !== userDivision) {
                console.log(`❌ FILTERED OUT: ${attendanceData.rollNumber} - Division ${studentDivision} (User division: ${userDivision})`);
                continue;
              } else {
                console.log(`✅ INCLUDED: ${attendanceData.rollNumber} - Division ${studentDivision} matches`);
              }
            }

            // Check if undertaking was already sent
            try {
              const notificationsRef = collection(db, 'notifications', attendanceData.rollNumber, 'messages');
              const notificationsQuery = query(notificationsRef, where('type', '==', 'undertaking_letter'));
              const notificationsSnapshot = await getDocs(notificationsQuery);
              sentMap[attendanceData.rollNumber] = !notificationsSnapshot.empty;
            } catch (err) {
              sentMap[attendanceData.rollNumber] = false;
            }

            defaultersList.push({
              rollNumber: attendanceData.rollNumber,
              studentName: attendanceData.studentName || `${studentData.firstName} ${studentData.surname}`,
              fatherName: studentData.fatherName || '',
              division: studentData.division || '',
              email: studentData.email || '',
              mobile: studentData.mobile || '',
              overallPercentage: overallPercentage.toFixed(2),
              courses: attendanceData.courses || [],
              theoryPercentage: attendanceData.theoryPercentage || 0,
              practicalPercentage: attendanceData.practicalPercentage || 0,
              theoryTotal: attendanceData.theoryTotal || {},
              practicalTotal: attendanceData.practicalTotal || {}
            });
          }
        }
      }

      defaultersList.sort((a, b) => parseFloat(a.overallPercentage) - parseFloat(b.overallPercentage));
      setDefaulters(defaultersList);
      setFilteredDefaulters(defaultersList);
      setSentUndertakings(sentMap);
    } catch (err) {
      console.error('Error fetching defaulters:', err);
      setError('Failed to load defaulters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterDefaulters = () => {
    let filtered = [...defaulters];

    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.rollNumber.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (divisionFilter !== 'all') {
      filtered = filtered.filter(student => student.division === divisionFilter);
    }

    setFilteredDefaulters(filtered);
  };

  const getUniqueDivisions = () => {
    const divisions = [...new Set(defaulters.map(s => s.division))].filter(Boolean);
    return divisions.sort();
  };

  const calculateAttendanceStats = (student) => {
    // Use data from Firestore if available
    if (student.theoryTotal && student.practicalTotal) {
      return {
        totalLecturesConducted: student.theoryTotal.conducted || 0,
        totalLecturesAttended: student.theoryTotal.attended || 0,
        totalPracticalsConducted: student.practicalTotal.conducted || 0,
        totalPracticalsAttended: student.practicalTotal.attended || 0
      };
    }

    // Fallback to calculating from courses
    const courses = student.courses || [];
    let totalLecturesConducted = 0;
    let totalLecturesAttended = 0;
    let totalPracticalsConducted = 0;
    let totalPracticalsAttended = 0;

    courses.forEach(course => {
      if (course.type === 'theory') {
        totalLecturesConducted += course.conducted || 0;
        totalLecturesAttended += course.attended || 0;
      } else if (course.type === 'practical') {
        totalPracticalsConducted += course.conducted || 0;
        totalPracticalsAttended += course.attended || 0;
      }
    });

    return {
      totalLecturesConducted,
      totalLecturesAttended,
      totalPracticalsConducted,
      totalPracticalsAttended
    };
  };

  const handleLetterAction = (student, type) => {
    setSelectedStudent(student);
    setLetterType(type);
    setShowModal(true);
    setShowConfirmation(false);
    setSuccessMessage('');
  };

  const handleDownload = () => {
    const printContent = document.getElementById('letter-content');
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleSendConfirmation = () => {
    setShowConfirmation(true);
  };

  const handleSendLetter = async () => {
    try {
      setSendingLetter(true);
      
      if (letterType === 'undertaking') {
        const notificationData = {
          type: 'undertaking_letter',
          title: '⚠️ Attendance Undertaking Required',
          message: `Your attendance is ${selectedStudent.overallPercentage}%. Download and submit the attendance undertaking letter signed by your parent/guardian immediately.`,
          letterData: {
            studentName: selectedStudent.studentName,
            fatherName: selectedStudent.fatherName,
            rollNumber: selectedStudent.rollNumber,
            division: selectedStudent.division,
            attendancePercentage: selectedStudent.overallPercentage,
            month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
            courses: selectedStudent.courses,
            theoryPercentage: selectedStudent.theoryPercentage,
            practicalPercentage: selectedStudent.practicalPercentage
          },
          sentBy: userName || 'Admin',
          createdAt: new Date(),
          isRead: false,
          priority: 'high'
        };

        const notificationsRef = collection(db, 'notifications', selectedStudent.rollNumber, 'messages');
        await addDoc(notificationsRef, notificationData);
        console.log('✅ Notification sent:', `notifications/${selectedStudent.rollNumber}/messages`);
      }

      setSuccessMessage('Undertaking Letter sent successfully! The student will see it in their notifications.');
      setShowConfirmation(false);
      
      // Update sent status
      setSentUndertakings(prev => ({
        ...prev,
        [selectedStudent.rollNumber]: true
      }));
      
      setTimeout(() => {
        setShowModal(false);
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error sending letter:', err);
      alert('Failed to send letter. Please try again.');
    } finally {
      setSendingLetter(false);
    }
  };

  const renderDeficiencyLetter = (student) => {
    const stats = calculateAttendanceStats(student);
    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    return (
      <div id="letter-content" className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Letterhead */}
        <div className="mb-6">
          <img 
            src="/ybit-letterhead.png" 
            alt="YBIT Letterhead" 
            className="w-full"
            style={{ maxHeight: '130px', objectFit: 'contain' }}
          />
        </div>

        <div className="">
          <div className="flex items-center justify-center mb-4">
            <div className="w-6 h-6 text-black-500 mr-2"></div>
            <h1 className="text-2xl font-bold text-black-500 underline">ATTENDANCE DEFICIENCY NOTICE</h1>
          </div>
          
         <div className="flex justify-between mb-4">
  <p className="font-semibold">Ref:</p>
  <p className="font-semibold">Date:_______________</p>
</div>

          <div className="mb-4">
            <p className="mb-1"><strong>To,</strong></p>
            <p>The Parent/Guardian of</p>
            <p className="font-semibold text-black-600">{student.studentName}</p>
            <p>Roll No: <span className="font-semibold text-black-600">{student.rollNumber}</span></p>
            <p>Division: <span className="font-semibold text-black-600">{student.division}</span></p>
          </div>

          <div className="mb-4">
            <p className="font-semibold underline">Subject: Notification of Low Attendance - Urgent Action Required</p>
          </div>

          <div className="mb-4">
            <p>Dear Parent/Guardian,</p>
            <p className="mt-2">
              This is to inform you that your ward, <strong>{student.studentName}</strong> (Roll No: <strong>{student.rollNumber}</strong>), 
              has been found deficient (POOR) in attendance for the current academic semester.
            </p>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg mb-4">
            <table className="w-full">
              <tbody>
                <tr className="border-b border-gray-300">
                  <td className="py-2 font-semibold">Total Lectures Conducted:</td>
                  <td className="py-2 text-right font-bold">{stats.totalLecturesConducted}</td>
                </tr>
                <tr className="border-b border-gray-300">
                  <td className="py-2 font-semibold">Total Lectures Attended:</td>
                  <td className="py-2 text-right font-bold">{stats.totalLecturesAttended}</td>
                </tr>
                <tr className="border-b border-gray-300">
                  <td className="py-2 font-semibold">Total Practicals Conducted:</td>
                  <td className="py-2 text-right font-bold">{stats.totalPracticalsConducted}</td>
                </tr>
                <tr className="border-b border-gray-300">
                  <td className="py-2 font-semibold">Total Practicals Attended:</td>
                  <td className="py-2 text-right font-bold">{stats.totalPracticalsAttended}</td>
                </tr>
                <tr className="border-t-2 border-gray-400 mt-2">
                  <td className="py-2 font-bold text-lg">Overall Attendance Percentage:</td>
                  <td className="py-2 text-right font-bold text-xl text-black-600">{student.overallPercentage}%</td>
                </tr>
                <tr>
                  <td className="py-2 font-semibold text-black-600">Minimum Required Attendance percentage:</td>
                  <td className="py-2 text-right font-semibold text-maroon-600">75%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mb-4">
            <p className="font-semibold mb-2">Important Points to Note:</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li><strong>University Regulations:</strong> Minimum 75% attendance per subject is required to be eligible for End Semester Examination(ESE).</li>
              <li><strong>Consequences:</strong> Continued shortage may lead to detention and your ward will not be allowed to sit for the ESE.</li>
              <li><strong>Immediate Action:</strong> Kindly ensure regular attendance henceforth.</li>
            </ol>
          </div>

          <div className="mt-8 pt-4 border-t border-gray-300 flex justify-between">
            <div className="text-center">
              <div className="border-t border-black pt-2 mt-16">
                <p className="font-semibold">Class Co-ordinator</p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-black pt-2 mt-16">
                <p className="font-semibold">Head of Department</p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-black pt-2 mt-16">
                <p className="font-semibold">Principal</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-xs text-center border-t-2 border-black pt-2">
          <p>Bhonsale Knowledge City Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi Dist:Sindhudurg 416510</p>
        </div>
      </div>
    );
  };

  const renderUndertakingLetter = (student) => {
    return (
      <div id="letter-content" className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
        <div className="text-center mb-6">
          <p className="font-semibold">Shri Yashwantrao Bhonsale Education Society's</p>
          <h1 className="text-2xl font-bold mb-2">YASHWANTRAO BHONSALE INSTITUTE OF TECHNOLOGY</h1>
          <p className="text-sm">(DTE CODE : 3470) (MSBTE CODE : 1742)</p>
          <p className="text-sm">Approved by AICTE, DTE & Affiliated to Mumbai University & MSBTE Mumbai</p>
          <p className="text-sm">(NBA Accredited ME, CE, EE Diploma Programs)</p>
          <p className="text-sm mb-2">ybinstitute.bkc@gmail.com</p>
          <p className="text-xs">Bhonsale Knowledge City Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi Dist:Sindhudurg 416510</p>
        </div>

        <h2 className="text-xl font-bold text-center mb-6 underline">Undertaking from the Parents Regarding 75% Attendance</h2>

        <div className="space-y-4">
          <div>
            <p>I, _____________________________________________________________________</p>
          </div>

          <div>
            <p>Father/Mother/Guardian of ________________________________________________</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <p>studying in class - <strong>First Year</strong></p>
            <p>Div-<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></p>
            <p>Roll No:<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></p>
            <p>Branch: <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></p>
          </div>

          <div className="flex flex-wrap gap-2">
            <p>Percentage of Attendance: <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></p>
            <p>Month: <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></p>
          </div>

          <p>hereby undertake:</p>

          <ol className="list-decimal ml-8 space-y-3">
            <li>I am aware that as per the Mumbai University norms <strong>minimum 75% attendance is compulsory.</strong></li>
            <li>I am also aware that if the attendance of my ward is less than the norms as specified above, his/her <strong>term will not be granted</strong> and he/she may <strong>not be permitted to appear</strong> for Mumbai University examination.</li>
            <li>I confirm that <strong>my ward is physically & mentally fit</strong> to attend this course and is in position to cope up with the course requirement.</li>
          </ol>

          <div className="mt-8 flex justify-between">
            <div>
              <p>Date- __________</p>
            </div>
            <div className="space-y-2">
              <p>Parent's Sign: __________________________</p>
              <p>Name:_________________________________</p>
              <p>Designation:____________________________</p>
              <p>Contact Nos: ____________________________</p>
              <p>Address: _______________________________</p>
              <p>_______________________________________</p>
              <p>_______________________________________</p>
            </div>
          </div>

          <div className="mt-8 flex justify-between border-t pt-4">
            <p>Date:</p>
            <p>Name & Signature of Student</p>
          </div>
        </div>

        <div className="mt-6 border-t pt-4 text-xs text-center">
          <p>Bhonsale Knowledge City Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi Dist:Sindhudurg 416510</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-3 text-gray-600">Loading defaulters...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12 text-red-500">
          <AlertTriangle className="w-6 h-6 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <AlertTriangle className="w-6 h-6 text-red-500 mr-2" />
          <h2 className="text-xl font-bold text-gray-800">Attendance Warning Letters</h2>
          {userRole === 'class_coordinator' && userDivision && (
            <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
              Division {userDivision} Only
            </span>
          )}
        </div>
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg">
          <span className="font-semibold">{filteredDefaulters.length}</span> Defaulter{filteredDefaulters.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {userRole === 'hod' && (
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={divisionFilter}
              onChange={(e) => setDivisionFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Divisions</option>
              {getUniqueDivisions().map(div => (
                <option key={div} value={div}>{div}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {filteredDefaulters.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className="text-lg font-semibold">No defaulters found!</p>
          <p className="text-sm">All students have attendance above 75%</p>
        </div>
      ) : (
        <div className="overflow-x-auto" style={{ maxHeight: '250px', overflowY: 'auto' }}>
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Roll No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Division</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Attendance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDefaulters.map((student) => (
                <tr key={student.rollNumber} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{student.rollNumber}</td>
                  <td className="px-4 py-3 text-sm">{student.studentName}</td>
                  <td className="px-4 py-3 text-sm">{student.division}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      parseFloat(student.overallPercentage) < 60 ? 'bg-red-100 text-red-700' :
                      parseFloat(student.overallPercentage) < 70 ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {student.overallPercentage}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleLetterAction(student, 'deficiency')}
                        className="flex items-center px-3 py-1.5 bg-red-500 text-white text-xs rounded-md hover:bg-red-600"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" />
                        Deficiency
                      </button>
                      <button
                        onClick={() => !sentUndertakings[student.rollNumber] && handleLetterAction(student, 'undertaking')}
                        disabled={sentUndertakings[student.rollNumber]}
                        className={`flex items-center px-3 py-1.5 text-xs rounded-md ${
                          sentUndertakings[student.rollNumber]
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        {sentUndertakings[student.rollNumber] ? (
                          <>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                            Sent
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5 mr-1" />
                            Undertaking
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center">
                <FileText className="w-6 h-6 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold">
                  {letterType === 'deficiency' ? 'Attendance Deficiency Letter' : 'Undertaking Letter'}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {letterType === 'deficiency' ? renderDeficiencyLetter(selectedStudent) : renderUndertakingLetter(selectedStudent)}
            </div>

            {successMessage && (
              <div className="px-6 py-4 bg-green-50 border-t">
                <div className="flex items-center text-green-700">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium">{successMessage}</span>
                </div>
              </div>
            )}

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end space-x-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDownload} className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800">
                <Download className="w-4 h-4 mr-2" />
                Download & Print
              </button>
              {letterType === 'undertaking' && !successMessage && (
                <button
                  onClick={handleSendConfirmation}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={sendingLetter}
                >
                  {sendingLetter ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send to Student
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-500 mr-2" />
              <h3 className="text-lg font-semibold">Confirm Send</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Send undertaking letter to <strong>{selectedStudent?.studentName}</strong>? It will appear in their notification bell.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
                disabled={sendingLetter}
              >
                Cancel
              </button>
              <button
                onClick={handleSendLetter}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={sendingLetter}
              >
                {sendingLetter ? 'Sending...' : 'Yes, Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarningLettersWidget;